<template>
  <el-dialog
    :visible="visible"
    @close="$emit('cancelled')"
    :title="tier ? $t('loyalty.button.edit_tier') : $t('loyalty.button.add_tier')"
    width="800px"
    append-to-body
    custom-class="tier-form-dialog">
    <div class="px-6 py-4">
      <el-form :model="form" :rules="rules" ref="tierForm" label-width="200px" class="tier-form">
        <el-form-item :label="$t('loyalty.label.tier_name')" prop="name" class="mb-6">
          <el-input
            v-model="form.name"
            maxlength="255"
            :placeholder="$t('loyalty.placeholder.tier_name')"
            class="w-full">
          </el-input>
        </el-form-item>

        <el-form-item :label="$t('loyalty.label.tier_code')" prop="code" class="mb-6">
          <el-input
            v-model="form.code"
            maxlength="50"
            :placeholder="$t('loyalty.placeholder.tier_code')"
            class="w-full">
          </el-input>
        </el-form-item>

        <el-form-item :label="$t('loyalty.label.description')" prop="description" class="mb-6">
          <el-input
            type="textarea"
            v-model="form.description"
            :rows="3"
            :placeholder="$t('loyalty.placeholder.description')"
            class="w-full">
          </el-input>
        </el-form-item>

        <div class="grid grid-cols-2 gap-6 mb-6">
          <el-form-item :label="$t('loyalty.label.min_points_required')" prop="min_points_required">
            <el-input-number
              v-model="form.min_points_required"
              :min="0"
              class="w-full">
            </el-input-number>
          </el-form-item>
          <el-form-item :label="$t('loyalty.label.sort_order')" prop="sort_order">
            <el-input-number
              v-model="form.sort_order"
              :min="0"
              class="w-full">
            </el-input-number>
          </el-form-item>
        </div>

        <div class="grid grid-cols-2 gap-6 mb-6">
          <el-form-item :label="$t('loyalty.label.points_multiplier')" prop="points_multiplier">
            <el-input-number
              v-model="form.points_multiplier"
              :min="0"
              :precision="2"
              class="w-full">
            </el-input-number>
            <p class="text-xs text-gray-500 mt-1">{{ $t('loyalty.placeholder.points_multiplier') }}</p>
          </el-form-item>
          <el-form-item :label="$t('loyalty.label.redemption_multiplier')" prop="redemption_multiplier">
            <el-input-number
              v-model="form.redemption_multiplier"
              :min="0"
              :precision="2"
              class="w-full">
            </el-input-number>
            <p class="text-xs text-gray-500 mt-1">{{ $t('loyalty.placeholder.redemption_multiplier') }}</p>
          </el-form-item>
        </div>

        <el-form-item :label="$t('loyalty.label.benefits')" prop="benefits" class="mb-6">
          <div class="w-full">
            <div
              v-for="(benefit, index) in benefitsList"
              :key="index"
              class="flex items-center space-x-2 mb-2">
              <el-input
                v-model="benefitsList[index]"
                :placeholder="$t('loyalty.placeholder.benefit')"
                class="flex-1">
              </el-input>
              <button
                @click="removeBenefit(index)"
                type="button"
                class="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                <i class="mdi mdi-delete"></i>
              </button>
            </div>
            <button
              @click="addBenefit"
              type="button"
              class="mt-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors">
              <i class="mdi mdi-plus mr-2"></i>
              {{ $t('loyalty.button.add_benefit') }}
            </button>
          </div>
        </el-form-item>

        <el-form-item :label="$t('loyalty.label.is_active')" prop="is_active" class="mb-6">
          <el-switch v-model="form.is_active"></el-switch>
        </el-form-item>
      </el-form>
    </div>
    <div slot="footer" class="dialog-footer px-6 py-4 bg-gray-50 flex justify-end space-x-3">
      <button
        @click="$emit('cancelled')"
        class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors">
        {{ $t('button.cancel') }}
      </button>
      <button
        @click="save"
        :disabled="saving"
        class="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
        <span v-if="saving" class="flex items-center">
          <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ $t('status.loading') }}
        </span>
        <span v-else>{{ $t('button.save') }}</span>
      </button>
    </div>
  </el-dialog>
</template>

<script>
import ErrorHandlingMixin from '@/mixins/ErrorHandlingMixin.js'

export default {
  name: 'TierFormDialog',
  mixins: [ErrorHandlingMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    tier: {
      type: Object,
      default: null
    },
    programId: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      saving: false,
      form: {
        name: '',
        code: '',
        description: '',
        min_points_required: 0,
        points_multiplier: 1.0,
        redemption_multiplier: 1.0,
        benefits: [],
        sort_order: 0,
        is_active: true
      },
      benefitsList: ['']
    }
  },
  computed: {
    rules () {
      return {
        name: [
          { required: true, message: this.$t('loyalty.validation.tier_name_required'), trigger: 'blur' },
          { max: 255, message: 'Name cannot exceed 255 characters', trigger: 'blur' }
        ],
        code: [
          { required: true, message: 'Tier code is required', trigger: 'blur' },
          { max: 50, message: 'Code cannot exceed 50 characters', trigger: 'blur' }
        ],
        min_points_required: [
          { required: true, message: this.$t('loyalty.validation.min_points_required_required'), trigger: 'blur' },
          { type: 'number', min: 0, message: this.$t('loyalty.validation.min_points_required_positive'), trigger: 'blur' }
        ],
        points_multiplier: [
          { required: true, message: 'Points multiplier is required', trigger: 'blur' },
          { type: 'number', min: 0, message: 'Must be positive', trigger: 'blur' }
        ],
        redemption_multiplier: [
          { required: true, message: 'Redemption multiplier is required', trigger: 'blur' },
          { type: 'number', min: 0, message: 'Must be positive', trigger: 'blur' }
        ]
      }
    }
  },
  watch: {
    tier: {
      immediate: true,
      handler (newVal) {
        if (newVal) {
          this.form = {
            name: newVal.name || '',
            code: newVal.code || '',
            description: newVal.description || '',
            min_points_required: newVal.min_points_required || 0,
            points_multiplier: parseFloat(newVal.points_multiplier) || 1.0,
            redemption_multiplier: parseFloat(newVal.redemption_multiplier) || 1.0,
            benefits: newVal.benefits || [],
            sort_order: newVal.sort_order || 0,
            is_active: newVal.is_active !== undefined ? newVal.is_active : true
          }
          this.benefitsList = Array.isArray(this.form.benefits) && this.form.benefits.length > 0
            ? [...this.form.benefits]
            : ['']
        } else {
          this.resetForm()
        }
      }
    },
    visible (newVal) {
      if (newVal && this.$refs.tierForm) {
        this.$refs.tierForm.clearValidate()
      }
    }
  },
  methods: {
    resetForm () {
      this.form = {
        name: '',
        code: '',
        description: '',
        min_points_required: 0,
        points_multiplier: 1.0,
        redemption_multiplier: 1.0,
        benefits: [],
        sort_order: 0,
        is_active: true
      }
      this.benefitsList = ['']
    },
    addBenefit () {
      this.benefitsList.push('')
    },
    removeBenefit (index) {
      this.benefitsList.splice(index, 1)
      if (this.benefitsList.length === 0) {
        this.benefitsList = ['']
      }
    },
    async save () {
      this.$refs.tierForm.validate(async (valid) => {
        if (!valid) return

        try {
          this.saving = true
          // Filter out empty benefits
          const benefits = this.benefitsList.filter(b => b && b.trim() !== '')
          const tierData = {
            ...this.form,
            benefits: benefits,
            loyalty_program_id: this.programId
          }
          let result
          if (this.tier) {
            result = await this.$store.dispatch('loyalty/tiers/updateTier', {
              id: this.tier.id,
              data: tierData
            })
            this.showSuccess(this.$t('loyalty.message.tier_updated'))
          } else {
            result = await this.$store.dispatch('loyalty/tiers/createTier', tierData)
            this.showSuccess(this.$t('loyalty.message.tier_created'))
          }
          this.$emit('saved', result)
        } catch (error) {
          console.error('Error saving tier:', error)
          this.globalErrHandle(error)
        } finally {
          this.saving = false
        }
      })
    }
  }
}
</script>

<style scoped>
.tier-form-dialog ::v-deep .el-dialog__body {
  padding: 0;
}

.tier-form ::v-deep .el-form-item__label {
  font-weight: 500;
  color: #374151;
}

.tier-form ::v-deep .el-input__inner,
.tier-form ::v-deep .el-textarea__inner {
  border-radius: 0.5rem;
  border-color: #d1d5db;
}

.tier-form ::v-deep .el-input__inner:focus,
.tier-form ::v-deep .el-textarea__inner:focus {
  border-color: #3b82f6;
}
</style>
